import React, { useEffect, useState } from 'react';
import { Outlet, Navigate, useSearchParams, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import CommandPalette from './CommandPalette';
import { cn } from '../lib/utils';
import UsageBanner from './UsageBanner';
import CoachThread from './v2/CoachThread';
import ViewingBanner from './appstate/ViewingBanner';
import CoachConnecting from './v2/CoachConnecting';
import { coachPanel } from '../v2/meReader';
import { useV2 } from '../context/V2Context';
import TrialEndedModal from './TrialEndedModal';
import TermsModal from './consent/TermsModal';
import { useAuth } from '../context/AuthContext';
import { useOnboarding } from '../context/OnboardingContext';
import { useCoaching } from '../context/CoachingContext';
import { layoutGate } from '../lib/retryLoader';
import StatusReconnecting from './StatusReconnecting';

const Layout = () => {
  // The coach panel publishes its layout footprint (CoachThread dispatches
  // `chat:layout` whenever it opens/closes or the view changes). We mirror it
  // here so <main> can give the sidebar real width instead of letting it cover
  // the page. Defaults to closed, so a missing event can never squeeze content.
  const [chatLayout, setChatLayout] = useState({ open: false, view: 'sidebar', rail: false });
  useEffect(() => {
    const onLayout = (e) => setChatLayout({
      open: Boolean(e.detail?.open),
      view: e.detail?.view || 'sidebar',
      rail: Boolean(e.detail?.rail),
    });
    window.addEventListener('chat:layout', onLayout);
    return () => window.removeEventListener('chat:layout', onLayout);
  }, []);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem('aithlo:sidebarCollapsed');
      return saved === null ? window.innerWidth < 1024 : saved === '1';
    } catch { return false; }
  });
  useEffect(() => {
    const onSidebar = (e) => setSidebarCollapsed(Boolean(e.detail?.collapsed));
    window.addEventListener('sidebar:layout', onSidebar);
    return () => window.removeEventListener('sidebar:layout', onSidebar);
  }, []);

  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const {
    fetchStatus,
    checkStravaStatus,
    completeStep,
    stravaConnected,
    onboardingCompleted,
    statusFailed,
    isLoading: onboardingLoading,
  } = useOnboarding();
  const { viewingAthlete, isViewingAthlete, isSwitchingAthlete, switchToSelf } = useCoaching();
  const { enabled: v2Enabled, coachMode, reconnecting, retryMe } = useV2();
  const panel = coachPanel({ coachMode, v2Enabled });
  const { logout } = useAuth() || {};

  // Fetch onboarding status on mount
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Strava OAuth is a full-page redirect that lands back here (e.g. from
  // /calendar) — clean up its return params and refresh the connection
  // status. If onboarding is still incomplete, the redirect below already
  // sends the athlete back to /onboarding, so there's no separate "reopen"
  // step needed here anymore.
  useEffect(() => {
    const stravaConnected = searchParams.get('strava');
    if (stravaConnected === 'connected') {
      searchParams.delete('strava');
      searchParams.delete('onboarding');
      setSearchParams(searchParams);
      checkStravaStatus();
    }
  }, [searchParams, setSearchParams, checkStravaStatus]);

  // Onboarding completion is tri-state (null = the status hasn't answered).
  // Unknown never opens the shell nor sends the athlete to onboarding: nothing
  // (no flash), then "Reconnecting…" while the read keeps failing and retrying.
  const gate = layoutGate({ completed: onboardingCompleted, failed: statusFailed });
  if (gate === "reconnecting") return <StatusReconnecting onRetry={fetchStatus} onLogout={logout} />;
  if (gate === "loading") return null;

  // While a refresh of a known status is in flight, render nothing (as before).
  if (onboardingLoading) return null;

  // Incomplete onboarding is a real navigation to its own page now, not an
  // overlay on top of the app shell — so Sidebar/the coach panel/banners below
  // never mount until onboarding is done.
  if (gate === "onboarding") return <Navigate to="/onboarding" replace />;

  return (
    <div className="flex min-h-screen bg-bg-canvas font-body">
      <Sidebar />
      <CommandPalette />
      <main
        className={cn(
          "flex-1 ml-0 pb-[calc(var(--mobile-nav-h)+0.75rem)] pl-(--safe-left) pr-(--safe-right) md:pb-0 overflow-x-hidden transition-[margin] duration-200",
          sidebarCollapsed ? "md:ml-16" : "md:ml-[232px]",
          // Reserve real width for the chat sidebar so the page reflows BESIDE
          // it instead of disappearing underneath. Matches the width on the
          // panel itself (CoachThread.jsx) — keep the two numbers in step. Only
          // from lg up, which is exactly where the sidebar view engages.
          "transition-[margin] duration-300",
          chatLayout.open && chatLayout.view === 'sidebar' && "lg:mr-[var(--coach-width,380px)]",
          // Closed, the coach is the 56px rail (CoachThread's lg:w-14).
          !chatLayout.open && chatLayout.rail && "lg:mr-14"
        )}
      >
        {isViewingAthlete && <ViewingBanner />}
        {isSwitchingAthlete && (
          <div className="fixed inset-0 z-40 bg-bg-canvas/40 backdrop-blur-[1px] pointer-events-none flex items-start justify-center pt-24">
            <div className="bg-bg-surface border border-border-default shadow-lg rounded-full px-4 py-2 flex items-center gap-2 text-sm text-text-body">
              <svg className="animate-spin h-4 w-4 text-accent-default" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.3" strokeWidth="3" />
                <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
              </svg>
              Loading {isViewingAthlete ? `${viewingAthlete?.username}'s` : 'your'} data…
            </div>
          </div>
        )}
        <UsageBanner />
        <div className={`p-4 md:p-6 lg:p-8 ${location.pathname.includes('route-editor') ? '' : 'max-w-7xl mx-auto'}`}>
          <Outlet />
        </div>
      </main>
      {/* The coach thread once its client runs; until then, a quiet spinner (#61). */}
      {panel === 'v2' && <CoachThread />}
      {panel === 'connecting' && (
        <CoachConnecting reconnecting={reconnecting} onRetry={retryMe} />
      )}
      {/* Explains, once a day, why the AI went quiet when a trial lapses. */}
      <TrialEndedModal />
      {/* "We've updated our terms": a consent missing at its current version, 18+ not
          confirmed, or the server refusing for want of the AI consent. Never for a
          coach viewing an athlete: the acceptance is the athlete's own. */}
      <TermsModal viewing={isViewingAthlete} onboarded={onboardingCompleted === true} />
    </div>
  );
};

export default Layout;